// §34.3 Step 1 - availability-first calendar: a 2-week strip by default with
// a month-view toggle, backed by ONE availability?from&to fetch per visible
// range (see useAvailability in ./api.ts - keyed by range, keepPreviousData
// so paging doesn't flash empty).
import { useMemo, useState } from 'react';
import { addDays, addMonths, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAvailability } from './api';
import { AvailabilityGrid } from './AvailabilityGrid';
import { Button } from '@/components/ui/button';
import { useLocaleFormatters } from '@/lib/format';

function toIsoDate(d: Date): string {
  return format(d, 'yyyy-MM-dd');
}

export function AvailabilityCalendar({
  workspaceId, preferredDate, selectedDate, onSelectDay, locationId,
}: {
  workspaceId: string | null;
  preferredDate: string | null;
  selectedDate: string | null;
  onSelectDay: (date: string) => void;
  locationId?: string;
}) {
  const { t } = useTranslation('jobs');
  const { formatDate } = useLocaleFormatters();
  const [mode, setMode] = useState<'strip' | 'month'>('strip');

  const today = useMemo(() => new Date(new Date().toDateString()), []);
  const preferred = preferredDate ? new Date(`${preferredDate}T00:00:00`) : null;
  const defaultStripEnd = addDays(today, 13);
  // §34.3 - if the customer's preferred date falls outside the default
  // 2-week strip, open the calendar already paged/scrolled to include it.
  const [anchor, setAnchor] = useState<Date>(() => (preferred && preferred > defaultStripEnd ? preferred : today));

  const rangeStart = mode === 'strip' ? anchor : startOfWeek(startOfMonth(anchor), { weekStartsOn: 1 });
  const rangeEnd = mode === 'strip' ? addDays(anchor, 13) : endOfWeek(endOfMonth(anchor), { weekStartsOn: 1 });
  const from = toIsoDate(rangeStart);
  const to = toIsoDate(rangeEnd);

  const { data: days = [] } = useAvailability(workspaceId, from, to, locationId);

  const preferredDay = preferredDate ? days.find((d) => d.date === preferredDate) : undefined;
  const preferredFullyBooked = preferredDay?.state === 'full';
  const nearestAvailable = preferredFullyBooked
    ? days.find((d) => d.date > preferredDate! && d.state !== 'full' && d.state !== 'closed')
    : undefined;

  function jumpTo(date: string) {
    setAnchor(new Date(`${date}T00:00:00`));
    onSelectDay(date);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <span className="font-display text-sm font-bold uppercase tracking-wide">
          {mode === 'strip' ? t('scheduling.twoWeekStrip') : formatDate(anchor, 'MMMM yyyy')}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setAnchor((a) => (mode === 'strip' ? addDays(a, -14) : addMonths(a, -1)))}
            className="grid h-7 w-7 place-items-center border border-border text-muted hover:bg-surface-2"
            aria-label={t('scheduling.prevRange')}
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            onClick={() => setAnchor(today)}
            className="border border-border px-2.5 text-xs font-semibold text-muted hover:bg-surface-2"
          >
            {t('scheduling.today')}
          </button>
          <button
            type="button"
            onClick={() => setAnchor((a) => (mode === 'strip' ? addDays(a, 14) : addMonths(a, 1)))}
            className="grid h-7 w-7 place-items-center border border-border text-muted hover:bg-surface-2"
            aria-label={t('scheduling.nextRange')}
          >
            <ChevronRight size={14} />
          </button>
          <Button size="sm" variant="outline" onClick={() => setMode((m) => (m === 'strip' ? 'month' : 'strip'))}>
            {mode === 'strip' ? t('scheduling.monthView') : t('scheduling.stripView')}
          </Button>
        </div>
      </div>

      {preferredFullyBooked ? (
        <div className="border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
          {nearestAvailable ? (
            <>
              {t('scheduling.preferredFullNotice', {
                date: formatDate(new Date(`${preferredDate}T00:00:00`), 'MMM d'),
                date2: formatDate(new Date(`${nearestAvailable.date}T00:00:00`), 'MMM d'),
              })}{' '}
              <button type="button" className="font-bold underline" onClick={() => jumpTo(nearestAvailable.date)}>
                {t('scheduling.jumpToDate')}
              </button>
            </>
          ) : (
            t('scheduling.preferredFullNoticeNoAlternative', { date: formatDate(new Date(`${preferredDate}T00:00:00`), 'MMM d') })
          )}
        </div>
      ) : null}

      {/* Desktop: horizontal strip or month grid. Mobile (<640px): vertical agenda list. */}
      <div className="hidden sm:block">
        <AvailabilityGrid days={days} selectedDate={selectedDate} preferredDate={preferredDate} onSelectDay={onSelectDay} layout={mode === 'strip' ? 'row' : 'grid'} />
      </div>
      <div className="sm:hidden">
        <AvailabilityGrid days={days} selectedDate={selectedDate} preferredDate={preferredDate} onSelectDay={onSelectDay} layout="agenda" />
      </div>
    </div>
  );
}
